// src/oura/newscan/useScanDraft.ts
//
// The one hook every New scan screen reads a draft through. It loads the
// draft, polls it while it is being written, and runs every write:
//   - one write at a time: writes queue behind each other, each sending the
//     version the previous one left
//   - a stale version reloads the latest and says so on the foot line
//   - lint and refused answers go back to the caller (shown inline) and to the
//     foot line
//   - a write's effect becomes `outcome`, the line under the decisions
//   - a success or info line on the foot clears itself after a few seconds;
//     an error stays until the next write or `clearMessage()`

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    acceptQuestionAlternative,
    answerScanDraftDecision,
    answerScanDraftPlan,
    approveScanDraft,
    discardScanDraft,
    getScanDraft,
    listQuestionAlternatives,
    patchScanDraftBusiness,
    patchScanDraftQuestion,
    patchScanDraftWord,
    rebuildScanDraft,
    refreshScanDraftSearch,
    reopenScanDraft,
    setScanDraftRecipe,
    startScanDraft,
    undoScanDraft,
} from "@service/aeo/scanDraft";
import type { RebuildOptions } from "@service/aeo/scanDraft";
import type {
    DecisionChoice,
    QuestionPatch,
    ScanDraft,
    ScanDraftAlternative,
    ScanDraftBusiness,
    ScanDraftEffect,
    ScanDraftFailure,
    ScanDraftResult,
    ScanDraftWrite,
    StartScanDraftRequest,
    StartScanDraftResult,
} from "@service/aeo/scanDraftTypes";
import { useVisiblePoll } from "@oura/lib/useVisiblePoll";
import { scanDraftPollMs } from "./flags";

/** What a running write is doing. Screens compare against these, so a new one goes here. */
export type BusyLabel = "Saving" | "Drafting" | "Undoing" | "Approving" | "Reopening" | "Starting" | "Discarding" | "Checking searches";

export interface ScanDraftMessage {
    tone: "success" | "error" | "info";
    text: string;
}

export interface ScanDraftController {
    draft: ScanDraft | null;
    loading: boolean;
    error: ScanDraftFailure | null;
    /** Short label of the running write, e.g. "Saving". */
    busy: BusyLabel | null;
    /** The last write's effect, cleared on reload. */
    outcome: ScanDraftEffect | null;
    /** For the aria-live foot line. */
    message: ScanDraftMessage | null;
    /** Empty the foot line, e.g. when the step changes. */
    clearMessage(): void;
    reload(): Promise<void>;
    patchBusiness(patch: Partial<ScanDraftBusiness>): Promise<ScanDraftFailure | null>;
    /** Draft the 50 again. `refreshWords` re-runs the word research; `refreshDemand` pulls the Google searches again (brand drafts, "Pull the searches again"). */
    rebuild(opts?: RebuildOptions): Promise<ScanDraftFailure | null>;
    patchQuestion(n: number, patch: QuestionPatch): Promise<ScanDraftFailure | null>;
    alternatives(n: number): Promise<ScanDraftResult<ScanDraftAlternative[]>>;
    acceptAlternative(n: number, altId: string): Promise<ScanDraftFailure | null>;
    patchWord(variable: string, value: string): Promise<ScanDraftFailure | null>;
    answer(decisionId: string, choice: DecisionChoice): Promise<ScanDraftFailure | null>;
    /** A first_check "For the plan" card with an edited list (priority keywords or outlet names). */
    answerPlan(decisionId: string, values: string[]): Promise<ScanDraftFailure | null>;
    setRecipe(mode: "take_all" | "keep_all" | "choose_each", picks?: Record<number, "take" | "keep">): Promise<ScanDraftFailure | null>;
    /**
     * Undo the latest change. `onLanded`: the draft version the undo produced, so a batch Undo can
     * re-anchor to it (Jack, #4451). The first argument is main's toast revision check, not on staging.
     */
    undo(expect?: undefined, onLanded?: (landed: { version: number }) => void): Promise<ScanDraftFailure | null>;
    approve(): Promise<ScanDraftFailure | null>;
    reopen(): Promise<ScanDraftFailure | null>;
    start(req: StartScanDraftRequest): Promise<ScanDraftResult<StartScanDraftResult>>;
    discard(): Promise<ScanDraftFailure | null>;
    refreshSearch(): Promise<ScanDraftFailure | null>;
}

/**
 * The draft as it will read once a word is saved: the word's value, and every
 * question that uses it with the blank (or the old value) replaced. Null when
 * there is nothing to show early.
 */
function withWord(d: ScanDraft, variable: string, value: string): ScanDraft | null {
    const word = d.words.find((w) => w.variable === variable);
    const next = value.replace(/\s+/g, " ").trim();
    if (!word || !next || next === word.value) return null;
    const old = word.value;
    return {
        ...d,
        words: d.words.map((w) => (w.variable === variable ? { ...w, value: next, origin: "staff" } : w)),
        questions: d.questions.map((q) => {
            if (q.locked || !q.fill_vars.includes(variable)) return q;
            const was = q.fill_values[variable] ?? old;
            const text = q.text.includes(`{${variable}}`)
                ? q.text.split(`{${variable}}`).join(next)
                : was && q.text.includes(was) ? q.text.split(was).join(next) : q.text;
            return { ...q, text, fill_values: { ...q.fill_values, [variable]: next } };
        }),
    };
}

const STALE_MESSAGE = "Someone else changed this draft. Showing the latest.";
const NOT_LOADED: ScanDraftFailure = { ok: false, kind: "absent", message: "The draft is not loaded yet." };
/** While nothing is being written, the poll only ticks this slowly (and does nothing). */
const IDLE_POLL_MS = 60_000;
/** How long a success or info line stays on the foot. */
const MESSAGE_MS = 6000;

/** The draft changes on its own: the drafting job runs, or the Search Console pull (`search.pulling`). */
function isMoving(d: ScanDraft | null): boolean {
    return d?.status === "building" || d?.search?.pulling === true;
}

export function useScanDraft(draftId: string | null): ScanDraftController {
    const [draft, setDraft] = useState<ScanDraft | null>(null);
    const [loading, setLoading] = useState<boolean>(draftId !== null);
    const [error, setError] = useState<ScanDraftFailure | null>(null);
    const [busy, setBusy] = useState<BusyLabel | null>(null);
    const [outcome, setOutcome] = useState<ScanDraftEffect | null>(null);
    const [message, setMessage] = useState<ScanDraftMessage | null>(null);

    const draftRef = useRef<ScanDraft | null>(null);
    const idRef = useRef<string | null>(draftId);
    idRef.current = draftId;
    const queue = useRef<Promise<unknown>>(Promise.resolve());

    /** Keep a read only if it is not older than what the screen already shows. */
    const show = useCallback((next: ScanDraft | null, force = false) => {
        const current = draftRef.current;
        if (!force && next && current && next.id === current.id && next.version < current.version) return;
        draftRef.current = next;
        setDraft(next);
    }, []);

    const read = useCallback(
        async (opts: { force: boolean; clearOutcome: boolean }) => {
            const id = idRef.current;
            if (!id) return;
            const res = await getScanDraft(id);
            if (idRef.current !== id) return;
            if (res.ok) {
                show(res.data, opts.force);
                setError(null);
            } else if (opts.force || !draftRef.current) {
                // A dropped poll keeps the draft on screen; only a reload with nothing to show is an error.
                setError(res);
            }
            if (opts.clearOutcome) setOutcome(null);
            setLoading(false);
        },
        [show],
    );

    const reload = useCallback(() => read({ force: true, clearOutcome: true }), [read]);

    useEffect(() => {
        draftRef.current = null;
        setDraft(null);
        setError(null);
        setBusy(null);
        setOutcome(null);
        setMessage(null);
        if (!draftId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        let cancelled = false;
        getScanDraft(draftId).then((res) => {
            if (cancelled) return;
            if (res.ok) show(res.data, true);
            else setError(res);
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [draftId, show]);

    const clearMessage = useCallback(() => setMessage(null), []);

    // A success or info line has been read out by then; an error waits for the next write.
    useEffect(() => {
        if (!message || message.tone === "error") return;
        const timer = window.setTimeout(() => setMessage((m) => (m === message ? null : m)), MESSAGE_MS);
        return () => window.clearTimeout(timer);
    }, [message]);

    // While the 50 are being written, or a year of searches is being pulled,
    // read the draft again (paused while the tab is hidden).
    const watching = isMoving(draft);
    useVisiblePoll(
        () => {
            if (isMoving(draftRef.current)) void read({ force: false, clearOutcome: false });
        },
        watching ? scanDraftPollMs() : IDLE_POLL_MS,
        [watching],
    );

    const onFailure = useCallback(
        async (failure: ScanDraftFailure) => {
            if (failure.kind === "stale") {
                setMessage({ tone: "info", text: STALE_MESSAGE });
                await read({ force: true, clearOutcome: true });
                return;
            }
            setMessage({ tone: "error", text: failure.message });
        },
        [read],
    );

    /** Run one call after the writes before it, with the draft as it stands then. */
    const enqueue = useCallback(
        <T,>(label: BusyLabel, call: (current: ScanDraft) => Promise<ScanDraftResult<T>>, onOk: (data: T) => void): Promise<ScanDraftResult<T>> => {
            // The draft the staffer acted on: a write queued for one draft never lands on another.
            const requestedId = idRef.current;
            const next = queue.current.then(async (): Promise<ScanDraftResult<T>> => {
                const current = draftRef.current;
                if (!requestedId || idRef.current !== requestedId || !current || current.id !== requestedId) return NOT_LOADED;
                setBusy(label);
                setMessage((m) => (m?.tone === "error" ? null : m));
                try {
                    const res = await call(current);
                    if (idRef.current !== requestedId) return NOT_LOADED;
                    if (res.ok) onOk(res.data);
                    else await onFailure(res);
                    return res;
                } finally {
                    if (idRef.current === requestedId) setBusy(null);
                }
            });
            queue.current = next.catch(() => undefined);
            return next;
        },
        [onFailure],
    );

    /** A write that answers with the whole draft and its effect. */
    const write = useCallback(
        async (label: BusyLabel, call: (current: ScanDraft) => Promise<ScanDraftResult<ScanDraftWrite>>): Promise<ScanDraftFailure | null> => {
            const res = await enqueue(label, call, (w) => {
                show(w.draft, true);
                setError(null);
                if (w.effect) {
                    setOutcome(w.effect);
                    setMessage({ tone: "success", text: w.effect.label });
                }
            });
            return res.ok ? null : res;
        },
        [enqueue, show],
    );

    const patchBusiness = useCallback(
        (patch: Partial<ScanDraftBusiness>) => write("Saving", (d) => patchScanDraftBusiness(d.id, d.version, patch)),
        [write],
    );
    const rebuild = useCallback((opts?: RebuildOptions) => write("Drafting", (d) => rebuildScanDraft(d.id, d.version, opts)), [write]);
    const patchQuestion = useCallback(
        (n: number, patch: QuestionPatch) => write("Saving", (d) => patchScanDraftQuestion(d.id, d.version, n, patch)),
        [write],
    );
    const alternatives = useCallback(async (n: number): Promise<ScanDraftResult<ScanDraftAlternative[]>> => {
        const current = draftRef.current;
        return current ? listQuestionAlternatives(current.id, n) : NOT_LOADED;
    }, []);
    const acceptAlternative = useCallback(
        (n: number, altId: string) => write("Saving", (d) => acceptQuestionAlternative(d.id, d.version, n, altId)),
        [write],
    );
    const patchWord = useCallback(
        async (variable: string, value: string) => {
            // Shown at once (the questions read the new word straight away); the server's draft replaces it,
            // and a refusal puts the draft back as it was.
            const before = draftRef.current;
            const guess = before ? withWord(before, variable, value) : null;
            if (guess) {
                draftRef.current = guess;
                setDraft(guess);
            }
            const failure = await write("Saving", (d) => patchScanDraftWord(d.id, d.version, variable, value));
            if (failure && guess && draftRef.current === guess) show(before, true);
            return failure;
        },
        [write, show],
    );
    const answer = useCallback(
        (decisionId: string, choice: DecisionChoice) => write("Saving", (d) => answerScanDraftDecision(d.id, d.version, decisionId, choice)),
        [write],
    );
    const answerPlan = useCallback(
        (decisionId: string, values: string[]) => write("Saving", (d) => answerScanDraftPlan(d.id, d.version, decisionId, values)),
        [write],
    );
    const setRecipe = useCallback(
        (mode: "take_all" | "keep_all" | "choose_each", picks?: Record<number, "take" | "keep">) =>
            write("Saving", (d) => setScanDraftRecipe(d.id, d.version, mode, picks)),
        [write],
    );
    const undo = useCallback(
        async (_expect?: undefined, onLanded?: (landed: { version: number }) => void): Promise<ScanDraftFailure | null> => {
            const res = await enqueue("Undoing", (d) => undoScanDraft(d.id, d.version), (w) => {
                onLanded?.({ version: w.draft.version });
                show(w.draft, true);
                setError(null);
                if (w.effect) {
                    setOutcome(w.effect);
                    setMessage({ tone: "success", text: w.effect.label });
                }
            });
            return res.ok ? null : res;
        },
        [enqueue, show],
    );
    const approve = useCallback(() => write("Approving", (d) => approveScanDraft(d.id, d.version)), [write]);
    const reopen = useCallback(() => write("Reopening", (d) => reopenScanDraft(d.id, d.version)), [write]);
    const refreshSearch = useCallback(() => write("Checking searches", (d) => refreshScanDraftSearch(d.id)), [write]);

    const start = useCallback(
        async (req: StartScanDraftRequest): Promise<ScanDraftResult<StartScanDraftResult>> => {
            const res = await enqueue("Starting", (d) => startScanDraft(d.id, d.version, req), () => {
                setMessage({ tone: "success", text: "The scan has started." });
            });
            if (res.ok) await read({ force: true, clearOutcome: false });
            return res;
        },
        [enqueue, read],
    );

    const discard = useCallback(async (): Promise<ScanDraftFailure | null> => {
        const res = await enqueue("Discarding", (d) => discardScanDraft(d.id, d.version), () => {
            show(null, true);
            setOutcome(null);
            setMessage({ tone: "info", text: "Draft discarded." });
        });
        return res.ok ? null : res;
    }, [enqueue, show]);

    return useMemo<ScanDraftController>(
        () => ({
            draft,
            loading,
            error,
            busy,
            outcome,
            message,
            clearMessage,
            reload,
            patchBusiness,
            rebuild,
            patchQuestion,
            alternatives,
            acceptAlternative,
            patchWord,
            answer,
            answerPlan,
            setRecipe,
            undo,
            approve,
            reopen,
            start,
            discard,
            refreshSearch,
        }),
        [
            draft,
            loading,
            error,
            busy,
            outcome,
            message,
            clearMessage,
            reload,
            patchBusiness,
            rebuild,
            patchQuestion,
            alternatives,
            acceptAlternative,
            patchWord,
            answer,
            answerPlan,
            setRecipe,
            undo,
            approve,
            reopen,
            start,
            discard,
            refreshSearch,
        ],
    );
}
